import type IGameSettings from 'types/models/IGameSettings';
import { useMemo } from 'react';
import {
  useSeasonalBranding,
  useSeasonalBrandingBadges,
  useSeasonalBrandingThumbnails,
} from 'store/featureFlags/featureFlagsStore';
import { getBadgeSectionName, getJackpotSlug, getNoJackpotException, SECTION_BADGE_REGISTRY } from 'features/games';

const getBadgeImagePrefix = (seasonalBranding?: string, useBranding?: boolean): string => {
  if (seasonalBranding && useBranding) return `/brandings/${seasonalBranding}/badges/`;
  return '/badges/';
};

type shouldBrandingReturnType = {
  shouldUseBrandingForThumbnails: true | undefined;
  shouldUseBrandingForBadges: true | undefined;
};

export function useShouldBranding(data: IGameSettings, effectiveData: IGameSettings): shouldBrandingReturnType {
  const seasonalBranding = useSeasonalBranding();
  const seasonalBrandingThumbnails = useSeasonalBrandingThumbnails();
  const seasonalBrandingBadges = useSeasonalBrandingBadges();

  return useMemo(() => {
    const jackpotName = (effectiveData.displayJackpotName || effectiveData.jackpotName || '').toLowerCase();

    return {
      shouldUseBrandingForThumbnails:
        (seasonalBranding && seasonalBrandingThumbnails[seasonalBranding]?.includes(data.gameKey)) || undefined,
      shouldUseBrandingForBadges:
        (seasonalBranding && seasonalBrandingBadges[seasonalBranding]?.includes(jackpotName)) || undefined,
    };
  }, [
    data.gameKey,
    effectiveData.displayJackpotName,
    effectiveData.jackpotName,
    seasonalBranding,
    seasonalBrandingThumbnails,
    seasonalBrandingBadges,
  ]);
}

export function useGameImageUrls(
  data: IGameSettings,
  effectiveData: IGameSettings,
  effectiveSectionName: string,
  shouldBranding: shouldBrandingReturnType,
) {
  const seasonalBranding = useSeasonalBranding();

  return useMemo(() => {
    const orientation: 'vertical' | 'horizontal' = 'vertical';
    const jackpot = effectiveData.displayJackpotName || effectiveData.jackpotName;
    const featureLabel = getNoJackpotException(data.gameFeatures);
    const label = jackpot || featureLabel;
    const slug = label ? getJackpotSlug(label) : null;

    // Main image path - only use branding if game is in thumbnails list
    const thumbnailImagePath =
      seasonalBranding && shouldBranding.shouldUseBrandingForThumbnails
        ? `/brandings/${seasonalBranding}/verticals/${data.gameKey}`
        : `/verticals/${data.gameKey}`;

    const badgeImagePrefix = getBadgeImagePrefix(seasonalBranding, shouldBranding.shouldUseBrandingForBadges);
    const badgeSectionName = getBadgeSectionName(
      { jackpotName: effectiveData.jackpotName, displayJackpotName: effectiveData.displayJackpotName },
      effectiveSectionName,
    );
    const badgeFileName = SECTION_BADGE_REGISTRY[badgeSectionName]?.fileName ?? slug;

    return {
      main: thumbnailImagePath,
      gameKey: data.gameKey,
      badgePlate: slug ? `${badgeImagePrefix}${badgeFileName}_plate` : null,
      badge: slug ? `${badgeImagePrefix}${badgeFileName}` : null,
      orientation,
    };
  }, [
    data.gameKey,
    data.gameFeatures,
    effectiveData.jackpotName,
    effectiveData.displayJackpotName,
    effectiveSectionName,
    shouldBranding,
  ]);
}
